import { useEffect, useState } from "react";
import { z } from "zod";

import { useAuth } from "../../contexts/authContext";

// `/user/user-info` answers a `{ message, user }` envelope although
// `getUserInfo` is typed as returning the user; `user` is null for an id that
// names no workspace member. Parsed here rather than in `getUserInfo` because
// its other callers read its result as typed.
const USER_INFO_RESPONSE = z.object({
  user: z.object({ email: z.string() }).nullable(),
});

interface LookedUpAuthor {
  userId: string;
  email: string;
}

/**
 * The name of the auditor whose override concluded a verdict, or null where the
 * verdict names no user, while the lookup is out, or where it failed.
 *
 * A user carries no display name, so the email stands for one, as it does on
 * the activity log. Pass null to look nothing up: the grid renders a conclusion
 * affordance per cell, and a lookup belongs only to a verdict whose author is
 * about to be shown.
 */
export function useOverrideAuthorName(
  userId: string | null | undefined,
): string | null {
  const { user, getUserInfo } = useAuth();
  const [lookedUp, setLookedUp] = useState<LookedUpAuthor | null>(null);

  const isCurrentUser = user !== null && user.id === userId;

  useEffect(() => {
    if (!userId || isCurrentUser) {
      return;
    }
    let cancelled = false;
    getUserInfo(userId)
      .then((response) => {
        const parsed = USER_INFO_RESPONSE.safeParse(response);
        if (!cancelled && parsed.success && parsed.data.user) {
          setLookedUp({ userId, email: parsed.data.user.email });
        }
      })
      .catch((error: unknown) => {
        console.error("Could not look up the overriding auditor:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, isCurrentUser, getUserInfo]);

  if (!userId) {
    return null;
  }
  if (isCurrentUser) {
    return user.email;
  }
  return lookedUp?.userId === userId ? lookedUp.email : null;
}
